import {userService} from 'services';
import tracker from 'tracking';
import Vue from 'vue';
import _ from 'lodash';
_.noConflict();

export const SET_USER = 'SET_USER';
export const SET_USER_PERMISSIONS = 'SET_USER_PERMISSIONS';
export const SET_USER_ROLE = 'SET_USER_ROLE';
export const FETCH_USER_ROLE = 'FETCH_USER_ROLE';
export const FETCH_USER_PERMISSIONS = 'FETCH_USER_PERMISSIONS';
export const FETCH_USER_NOTES = 'FETCH_USER_NOTES';
export const SET_USER_NOTES = 'SET_USER_NOTES';
export const SET_RELEASE_NOTE_SEEN = 'SET_RELEASE_NOTE_SEEN';
export const SAVE_USER_NOTES = 'SAVE_USER_NOTES';
export const SET_ONBOARDING_SEEN = 'SET_ONBOARDING_SEEN';
export const SET_NEED_QUICKSTART = 'SET_NEED_QUICKSTART';
export const SET_TECH_WORKS_VIEWED_LIST = 'SET_TECH_WORKS_VIEWED_LIST';
export const SET_VAT_INCREASE_NOTIFY_VIEWED_2026 = 'SET_VAT_INCREASE_NOTIFY_VIEWED_2026';
export const SET_META_USER = 'SET_META_USER';
export const GET_META_USER = 'GET_META_USER';
export const SET_TO_STORE_USER_META = 'SET_TO_STORE_USER_META';
export const SET_CONNECTION_WIZARD_DRAFT = 'SET_CONNECTION_WIZARD_DRAFT';
export const CLEAR_CONNECTION_WIZARD_DRAFT = 'CLEAR_CONNECTION_WIZARD_DRAFT';

function trackUserRoleChange (context) {
  const role = context.getters.userRole || {};
  if (!role.id && context.getters.currentUserIsAdmin) {
    tracker.setRole({...role, name: 'Суперадмин'});
  } else {
    tracker.setRole(role);
  }
}

function trackUserChange (context) {
  const user = context.getters.currentUser;
  tracker.setUser({id: user.id, email: user.email, created_date: user.created_at});
}

const userAbortController = new AbortController();

const userStore = {
  namespaced: true,
  state: {
    user: null,
    permissions: {
      idsSet: new Set([]),
      items: []
    },
    userRole: null,
    userMeta: {},
    userNotes: {},
    connectionWizardDraft: null
  },
  getters: {
    techWorksNotViewed (state, getters) {
      if (!getters.currentUser?.tech_works?.length) {
        return [];
      }

      if (getters.techWorksViewedList?.length) {
        return getters.currentUser?.tech_works.filter(item => !getters.techWorksViewedList.includes(item.id));
      }

      return getters.currentUser?.tech_works || [];
    },
    techWorksViewedList (state) {
      return state.userNotes?.tech_works_viewed_list || [];
    },
    isVATIncreaseNotifyViewed2026 (state) {
      return state.userMeta?.vat_increase_notify_viewed_2026 ?? false;
    },
    currentUser (state) {
      return state.user;
    },
    currentUserIsAdmin (state, getters) {
      return getters.currentUser.is_admin;
    },
    userLocale (state) {
      return state.user.locale;
    },
    userHelpUrl (state) {
      return state.user.help_url;
    },
    canUser (state) {
      return (p) => {
        if (Array.isArray(p)) {
          return p.every(i => state.permissions.idsSet.has(i));
        }
        return state.permissions.idsSet.has(p);
      };
    },
    permissions: state => state.permissions,
    userRole: state => state.userRole,
    userNotes: state => state.userNotes,
    userReleaseNotes (state, getters) {
      const notes = getters.userNotes;
      return _.keyBy(notes.release_notes, 'id') || {};
    },
    userOnboardings (state, getters) {
      const notes = getters.userNotes;
      return _.keyBy(notes.onboardings, 'id') || {};
    },
    userNewFeatureMarks (state, getters) {
      const notes = getters.userNotes;
      return _.keyBy(notes.new_feature_marks, 'id') || {};
    },
    userOnboardingMenu (state, getters) {
      return getters.userNotes.onboarding_menu || {};
    },
    connectionWizardDraft (state) {
      return state.connectionWizardDraft;
    }
  },
  mutations: {
    [SET_USER] (state, user) {
      state.user = user;
    },
    [SET_USER_PERMISSIONS] (state, permissions) {
      const permissionsSet = new Set(permissions.map(i => i.id));

      state.permissions = {
        idsSet: permissionsSet,
        items: permissions
      };
    },
    [SET_USER_ROLE] (state, userRole) {
      state.userRole = userRole;
    },
    [SET_USER_NOTES] (state, notes) {
      state.userNotes = notes;
    },
    [SET_RELEASE_NOTE_SEEN] (state, releaseNoteId) {
      const updateNote = state.userNotes.release_notes.find(i => i.id === releaseNoteId);
      if (updateNote) {
        updateNote.seen = true;
        return;
      }
      state.userNotes.release_notes = [
        ...state.userNotes.release_notes,
        {id: releaseNoteId, seen: true}
      ];
    },
    [SET_ONBOARDING_SEEN] (state, [onboardingId, meta]) {
      const updateOnboarding = state.userNotes.onboardings.find(i => i.id === onboardingId);
      if (updateOnboarding) {
        Object.keys(meta).forEach(key => {
          updateOnboarding[key] = meta[key];
        });
        return;
      }

      state.userNotes.onboardings = [
        ...state.userNotes.onboardings,
        {
          id: onboardingId,
          ...meta,
          seen: true
        }
      ];
    },
    [SET_TO_STORE_USER_META] (state, userMeta) {
      state.userMeta = userMeta;
    },
    SET_NEW_FEATURE_MARKER_SEEN (state, markerId) {
      const updateMarker = state.userNotes.new_feature_marks.find(i => i.id === markerId);
      if (updateMarker) {
        updateMarker.seen = true;
        return;
      }
      state.userNotes.new_feature_marks = [
        ...state.userNotes.new_feature_marks,
        {
          id: markerId,
          seen: true
        }
      ];
    },
    [SET_TECH_WORKS_VIEWED_LIST] (state, newTechWorksViewedList) {
      state.userNotes.tech_works_viewed_list = [...state.userNotes.tech_works_viewed_list, ...newTechWorksViewedList];
    },
    [SET_NEED_QUICKSTART] (state, need) {
      state.user.need_quickstart = need;
    },
    SET_ONBOARDING_MENU_STATE (state, menuState) {
      Vue.set(state.userNotes, 'onboarding_menu', menuState);
    },
    [SET_VAT_INCREASE_NOTIFY_VIEWED_2026] (state, isViewed) {
      state.userMeta.vat_increase_notify_viewed_2026 = isViewed;
    },
    [SET_CONNECTION_WIZARD_DRAFT] (state, draft) {
      state.connectionWizardDraft = draft;
    },
    [CLEAR_CONNECTION_WIZARD_DRAFT] (state) {
      state.connectionWizardDraft = null;
    }
  },
  actions: {
    [SET_USER] (context, user) {
      context.commit('SET_USER', user);
      trackUserChange(context);
    },
    [SET_USER_PERMISSIONS] (context, permissions) {
      context.commit('SET_USER_PERMISSIONS', permissions);
    },
    [SET_NEED_QUICKSTART] (context, need) {
      context.commit(SET_NEED_QUICKSTART, need);
    },
    [SET_TO_STORE_USER_META] (context, userMeta) {
      context.commit(SET_TO_STORE_USER_META, userMeta);
    },
    async [FETCH_USER_ROLE] (context) {
      const accountId = context.rootGetters['accounts/currentAccountId'];
      if (accountId) {
        const role = await userService.getUserRole(context.rootGetters['accounts/currentAccountId']);
        context.commit(SET_USER_ROLE, role);
        trackUserRoleChange(context);
        return;
      }
      context.commit(SET_USER_ROLE, null);
      trackUserRoleChange(context);
    },
    async [FETCH_USER_PERMISSIONS] (context) {
      const permissions = (await userService.getUserPermissions(context.rootGetters['accounts/currentAccountId'])).items;
      context.commit(SET_USER_PERMISSIONS, permissions);
    },
    async [FETCH_USER_NOTES] (context) {
      const notes = (await userService.getUserNotes()).data;
      context.commit(SET_USER_NOTES, notes);
    },
    async [SAVE_USER_NOTES] (context) {
      const userNotes = context.getters.userNotes;
      const updated = (await userService.saveUserNotes(userNotes, {signal: userAbortController.signal})).data.data;
      context.commit(SET_USER_NOTES, updated);
    },
    async [SET_RELEASE_NOTE_SEEN] (context, releaseNoteId) {
      context.commit(SET_RELEASE_NOTE_SEEN, releaseNoteId);
      await context.dispatch(SAVE_USER_NOTES);
    },
    async [SET_ONBOARDING_SEEN] (context, [onboardingId, meta]) {
      context.commit(SET_ONBOARDING_SEEN, [onboardingId, meta]);
      await context.dispatch(SAVE_USER_NOTES);
    },
    async [SET_TECH_WORKS_VIEWED_LIST] (context, newTechWorksViewedList) {
      context.commit(SET_TECH_WORKS_VIEWED_LIST, newTechWorksViewedList);
      await context.dispatch(SAVE_USER_NOTES);
    },
    async [SET_VAT_INCREASE_NOTIFY_VIEWED_2026] (context, isViewed) {
      context.commit(SET_VAT_INCREASE_NOTIFY_VIEWED_2026, isViewed);
      await context.dispatch(SAVE_USER_NOTES);
    },
    async SET_NEW_FEATURE_MARKER_SEEN (context, markerId) {
      context.commit('SET_NEW_FEATURE_MARKER_SEEN', markerId);
      await context.dispatch(SAVE_USER_NOTES);
    },
    async [SET_META_USER] (context, payload) {
      const userMeta = await userService.setMeta(payload);
      context.dispatch(SET_TO_STORE_USER_META, userMeta.meta);

      return context.getters.userMeta;
    },
    async SET_ONBOARDING_MENU_STATE (context, state) {
      context.commit('SET_ONBOARDING_MENU_STATE', state);
      await context.dispatch(SAVE_USER_NOTES);
    },
    async [GET_META_USER] (context) {
      const userMeta = await userService.getMeta();
      context.dispatch(SET_TO_STORE_USER_META, userMeta.meta);

      return userMeta;
    },
    [SET_CONNECTION_WIZARD_DRAFT] (context, draft) {
      context.commit(SET_CONNECTION_WIZARD_DRAFT, draft);
    },
    [CLEAR_CONNECTION_WIZARD_DRAFT] (context) {
      context.commit(CLEAR_CONNECTION_WIZARD_DRAFT);
    }
  }
};

export default userStore;
